import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, CheckCircle, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { logger } from "@/lib/logger";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const deviceTypes = [
  { value: "telephone", label: "Téléphone" },
  { value: "ordinateur", label: "Ordinateur" },
  { value: "tablette", label: "Tablette" },
  { value: "console", label: "Console de jeux" },
  { value: "autre", label: "Autre" }
];

const urgencyLevels = [
  { value: "normal", label: "Normal (3-5 jours)" },
  { value: "express", label: "Express (24-48h)" },
  { value: "urgent", label: "Urgent (même jour)" }
];

export const QuoteForm = ({ compact = false }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    device_type: "",
    device_brand: "",
    device_model: "",
    issue_description: "",
    urgency: "normal"
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/quote`, formData);
      setIsSuccess(true);
      toast.success("Demande de devis envoyée !", {
        description: "Nous vous recontacterons dans les plus brefs délais."
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        device_type: "",
        device_brand: "",
        device_model: "",
        issue_description: "",
        urgency: "normal"
      });
      setTimeout(() => setIsSuccess(false), 3000);
    } catch (error) {
      logger.error("Error submitting quote:", error);
      toast.error("Erreur lors de l'envoi", {
        description: "Veuillez réessayer ou nous appeler directement."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (compact) {
    return (
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="quote-form-compact">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="name">Nom *</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Votre nom"
              required
              data-testid="quote-name-input"
            />
          </div>
          <div>
            <Label htmlFor="phone">Téléphone *</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="06 12 34 56 78"
              required
              data-testid="quote-phone-input"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="device_type">Type d&apos;appareil *</Label>
          <Select value={formData.device_type} onValueChange={(v) => handleSelectChange("device_type", v)}>
            <SelectTrigger data-testid="quote-device-select">
              <SelectValue placeholder="Sélectionner..." />
            </SelectTrigger>
            <SelectContent>
              {deviceTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="issue_description">Description du problème *</Label>
          <Textarea
            id="issue_description"
            name="issue_description"
            value={formData.issue_description}
            onChange={handleChange}
            placeholder="Décrivez votre problème..."
            rows={3}
            required
            data-testid="quote-issue-textarea"
          />
        </div>
        <Button 
          type="submit" 
          className="w-full bg-blue-600 hover:bg-blue-700"
          disabled={isSubmitting || !formData.device_type}
          data-testid="quote-submit-btn"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Envoi...
            </>
          ) : isSuccess ? (
            <>
              <CheckCircle className="mr-2 h-4 w-4" />
              Envoyé !
            </>
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" />
              Demander un devis gratuit
            </>
          )}
        </Button>
      </form>
    );
  }

  return (
    <Card className="w-full" data-testid="quote-form-full">
      <CardHeader>
        <CardTitle>Demander un devis gratuit</CardTitle>
        <CardDescription>
          Remplissez ce formulaire et recevez votre devis sous 24h
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="name">Nom complet *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Jean Dupont"
                required
                data-testid="quote-full-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="jean@email.com"
                required
                data-testid="quote-full-email-input"
              />
            </div>
            <div>
              <Label htmlFor="phone">Téléphone *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="06 12 34 56 78"
                required
                data-testid="quote-full-phone-input"
              />
            </div>
          </div>

          {/* Device Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="device_type">Type d&apos;appareil *</Label>
              <Select value={formData.device_type} onValueChange={(v) => handleSelectChange("device_type", v)}>
                <SelectTrigger data-testid="quote-full-device-select">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {deviceTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="device_brand">Marque *</Label>
              <Input
                id="device_brand"
                name="device_brand"
                value={formData.device_brand}
                onChange={handleChange}
                placeholder="Apple, Samsung, HP..."
                required
                data-testid="quote-full-brand-input"
              />
            </div>
            <div>
              <Label htmlFor="device_model">Modèle *</Label>
              <Input
                id="device_model"
                name="device_model"
                value={formData.device_model}
                onChange={handleChange}
                placeholder="iPhone 15, Galaxy S24..."
                required
                data-testid="quote-full-model-input"
              />
            </div>
          </div>

          {/* Urgency */}
          <div>
            <Label htmlFor="urgency">Niveau d&apos;urgence</Label>
            <Select value={formData.urgency} onValueChange={(v) => handleSelectChange("urgency", v)}>
              <SelectTrigger data-testid="quote-full-urgency-select">
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                {urgencyLevels.map((level) => (
                  <SelectItem key={level.value} value={level.value}>
                    {level.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Problem Description */}
          <div>
            <Label htmlFor="issue_description">Description du problème *</Label>
            <Textarea
              id="issue_description"
              name="issue_description"
              value={formData.issue_description}
              onChange={handleChange}
              placeholder="Décrivez en détail le problème rencontré avec votre appareil..."
              rows={4}
              required
              data-testid="quote-full-issue-textarea"
            />
          </div>

          <Button 
            type="submit" 
            size="lg"
            className="w-full bg-blue-600 hover:bg-blue-700 h-12"
            disabled={isSubmitting || !formData.device_type}
            data-testid="quote-full-submit-btn"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Envoi en cours...
              </>
            ) : isSuccess ? (
              <>
                <CheckCircle className="mr-2 h-5 w-5" />
                Demande envoyée !
              </>
            ) : (
              <>
                <Send className="mr-2 h-5 w-5" />
                Envoyer ma demande de devis
              </>
            )}
          </Button>

          <p className="text-sm text-slate-500 text-center">
            Devis gratuit et sans engagement. Réponse sous 24h.
          </p>
        </form>
      </CardContent>
    </Card>
  );
};
